import React from "react"
import { Alert, Box, Button, IconButton, Paper, Stack, Step, StepLabel, Stepper, Typography } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CloseIcon from "@mui/icons-material/Close"

import type { SkuCostConfig } from "@/features/profit-calculators/calculator-shared/types"
import PlatformIcon from "@/features/profit-calculators/calculator-shared/components/PlatformIcon"

export interface UniqueSku {
  sku: string
  productName: string
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** Converts a "YYYY-MM" route param into the "Mon YYYY" label used by report compilers. */
export const monthKeyToLabel = (monthKey: string): string => {
  const [year, month] = monthKey.split("-")
  const monthIndex = parseInt(month, 10) - 1
  return `${MONTH_NAMES[monthIndex] ?? "Jan"} ${year}`
}

export const loadSkuCosts = (storageKey: string): Record<string, SkuCostConfig> => {
  try {
    const saved = localStorage.getItem(storageKey)
    return saved ? JSON.parse(saved) : {}
  } catch {
    return {}
  }
}

export const buildInitialSkuCosts = (uniqueSkus: UniqueSku[], storageKey: string): Record<string, SkuCostConfig> => {
  const savedCosts = loadSkuCosts(storageKey)
  return Object.fromEntries(
    uniqueSkus.map(({ sku, productName }) => [
      sku,
      savedCosts[sku] || {
        sku,
        productName,
        productPrice: 0,
        productGst: 18,
        packingPrice: 0,
        packingGst: 18,
        otherPrice: 0
      }
    ])
  )
}

export const persistSkuCosts = (storageKey: string, costs: Record<string, SkuCostConfig>) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ ...loadSkuCosts(storageKey), ...costs }))
  } catch (error) {
    console.error("Failed to save sku costs to localStorage", error)
  }
}

interface WizardHeaderProps {
  activeStep: number
  finalStep: number
  steps: string[]
  platformName: "Amazon" | "Meesho"
  error: string | null
  generateError: string | null
  generating: boolean
  onClearError: () => void
  onClearGenerateError: () => void
  onRetryGenerate: () => void
}

export const CalculatorWizardHeader: React.FC<WizardHeaderProps> = ({
  activeStep,
  finalStep,
  steps,
  platformName,
  error,
  generateError,
  generating,
  onClearError,
  onClearGenerateError,
  onRetryGenerate
}) => (
  <>
    {activeStep < finalStep && (
      <Stack spacing={0.5} sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <PlatformIcon platform={platformName === "Amazon" ? "amazon" : "meesho"} />
          <Typography variant="h5" fontWeight={800}>
            {platformName} Profit Calculator
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Process {platformName} files to audit exact profit summaries
        </Typography>
      </Stack>
    )}

    {activeStep < finalStep && (
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 5 }}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    )}

    {error && (
      <Alert severity="error" onClose={onClearError} sx={{ mb: 3, borderRadius: 2 }}>
        {error}
      </Alert>
    )}

    {generateError && (
      <Alert
        severity="error"
        onClose={onClearGenerateError}
        action={
          <Button color="inherit" size="small" onClick={onRetryGenerate} disabled={generating}>
            Retry
          </Button>
        }
        sx={{ mb: 3, borderRadius: 2 }}>
        {generateError}
      </Alert>
    )}
  </>
)

interface SkuCostsStepProps {
  fileName: string
  orderCount: number
  uniqueSkuCount: number
  costsConfigured: boolean
  nextStepLabel: string
  onConfigure: () => void
  onRemoveFile: () => void
  onBack: () => void
  onNext: () => void
}

export const SkuCostsStep: React.FC<SkuCostsStepProps> = ({
  fileName,
  orderCount,
  uniqueSkuCount,
  costsConfigured,
  nextStepLabel,
  onConfigure,
  onRemoveFile,
  onBack,
  onNext
}) => (
  <Stack spacing={3}>
    <Typography variant="h6" fontWeight={750}>
      Step 2: Enter SKU Product &amp; Packing Costs
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Assign the landed cost of goods sold (COGS) for each unique SKU found in your Orders report.
    </Typography>

    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Orders File Loaded
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            File: <code>{fileName}</code> | {orderCount} records | {uniqueSkuCount} unique SKUs
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Button
            variant="contained"
            onClick={onConfigure}
            startIcon={costsConfigured ? <CheckCircleIcon /> : undefined}
            color={costsConfigured ? "success" : "primary"}
            sx={{ borderRadius: 2 }}>
            {costsConfigured ? "Edit Cost Settings" : "Configure SKU Costs"}
          </Button>
          <IconButton onClick={onRemoveFile} color="error" title="Remove Orders file">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>

    {costsConfigured && (
      <Alert severity="success" sx={{ borderRadius: 2 }}>
        Product costs successfully saved for all {uniqueSkuCount} SKUs! You can now proceed to the next step.
      </Alert>
    )}

    <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
      <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ borderRadius: 2 }}>
        Back
      </Button>
      <Button
        variant="contained"
        endIcon={<ArrowForwardIcon />}
        disabled={!costsConfigured}
        onClick={onNext}
        sx={{ borderRadius: 2 }}>
        Proceed to {nextStepLabel}
      </Button>
    </Stack>
  </Stack>
)
